import { useState } from 'react';
import { savedDeals, DealItem } from '../../../mocks/dashboard';

const STATUS_STYLES: Record<DealItem['status'], string> = {
  Watching: 'bg-white/10 text-white/60',
  Bidding:  'bg-gold/20 text-gold',
  Won:      'bg-green-500/20 text-green-400',
  Passed:   'bg-white/5 text-white/25',
};
const STATE_PILL: Record<string, string> = {
  CA: 'bg-amber-500/15 text-amber-400',
  NV: 'bg-red-500/15 text-red-400',
  AZ: 'bg-orange-500/15 text-orange-400',
};

const fmt = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2)}M`
    : `$${(n / 1_000).toFixed(0)}K`;

type FilterStatus = 'All' | DealItem['status'];
const FILTERS: FilterStatus[] = ['All', 'Watching', 'Bidding', 'Won', 'Passed'];

export default function MyDealsContent() {
  const [filter, setFilter] = useState<FilterStatus>('All');

  const visible = filter === 'All'
    ? savedDeals
    : savedDeals.filter((d) => d.status === filter);

  return (
    <div className="flex flex-col gap-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-playfair text-3xl font-bold text-white">My Deals</h1>
          <p className="text-white/40 text-sm mt-1">{savedDeals.length} properties tracked across CA, NV &amp; AZ</p>
        </div>
        <a
          href="/property-listings"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy-dark text-sm font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap flex-shrink-0"
        >
          <i className="ri-add-line" />
          Add Property
        </a>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              filter === f
                ? 'bg-gold border-gold text-navy-dark'
                : 'border-white/10 text-white/40 hover:border-white/30 hover:text-white/70'
            }`}
          >
            {f}
            {f !== 'All' && (
              <span className="ml-1.5 opacity-60">
                ({savedDeals.filter((d) => d.status === f).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Deals grid */}
      {visible.length === 0 ? (
        <div className="text-center py-20">
          <i className="ri-building-2-line text-white/10 text-6xl mb-4 block" />
          <p className="text-white/30">No deals with status &ldquo;{filter}&rdquo;</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {visible.map((deal) => (
            <div
              key={deal.id}
              className="bg-white/4 border border-white/8 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-200"
            >
              {/* Image */}
              <div className="relative h-40 overflow-hidden">
                <img
                  src={deal.imageUrl}
                  alt={deal.address}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                {/* Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-md ${STATE_PILL[deal.state]}`}>
                    {deal.state}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-md ${STATUS_STYLES[deal.status]}`}>
                    {deal.status}
                  </span>
                </div>

                {/* Equity badge */}
                <div className="absolute bottom-3 right-3">
                  <span className="flex items-center gap-1 text-green-400 text-xs font-bold bg-black/50 px-2 py-0.5 rounded-md backdrop-blur-sm">
                    <i className="ri-arrow-up-line text-xs" />
                    {deal.equityPct.toFixed(1)}% equity
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <p className="text-white font-semibold text-sm mb-0.5 truncate">{deal.address}</p>
                <p className="text-white/40 text-xs mb-4 flex items-center gap-1">
                  <i className="ri-map-pin-line text-gold text-xs" />
                  {deal.city}, {deal.state}
                </p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white/5 rounded-lg p-2.5">
                    <p className="text-white/30 text-xs">Market Value</p>
                    <p className="text-white font-bold text-sm mt-0.5">{fmt(deal.marketValue)}</p>
                  </div>
                  <div className="bg-gold/8 rounded-lg p-2.5">
                    <p className="text-white/30 text-xs">Current Bid</p>
                    <p className="text-gold font-bold text-sm mt-0.5">{fmt(deal.currentBid)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="flex items-center gap-1 text-white/35 text-xs">
                    <i className="ri-auction-line text-xs" />
                    {deal.status === 'Won' || deal.status === 'Passed'
                      ? deal.status
                      : `Auction in ${deal.auctionDays}d`}
                  </span>
                  <a
                    href="/property-listings"
                    className="text-xs text-gold/70 font-semibold hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View Details →
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
